import { useColorScheme } from '@mui/material';
import { useCallback, useState } from 'react';

import { getCookie, setCookie } from '@infor/utils/src/cookies';

import type { SettingsState } from '../../types';

type UseSettingsManagerProps = {
  storageKey: string;
  initialSettings: SettingsState;
};

export function useSettingsManager({
  storageKey,
  initialSettings,
}: UseSettingsManagerProps) {
  const { mode, setMode } = useColorScheme();

  const [stateApp, setStateApp] = useState<SettingsState>(() => {
    const colorScheme =
      mode == initialSettings.colorScheme ? mode : initialSettings.colorScheme;

    setMode(colorScheme === 'dark' ? 'dark' : 'light');

    setCookie(storageKey, initialSettings);

    return initialSettings;
  });

  const resetState = useCallback(
    (defaultSettingsApp: SettingsState) => {
      setStateApp(defaultSettingsApp);

      setCookie(storageKey, defaultSettingsApp);
    },
    [storageKey]
  );

  const setField = useCallback(
    (
      name: keyof SettingsState,
      updateValue: SettingsState[keyof SettingsState]
    ) => {
      setStateApp((prev) => ({ ...prev, [name]: updateValue }));

      const storageValue = getCookie<SettingsState>(storageKey);

      if (name === 'colorScheme') {
        setMode(updateValue === 'dark' ? 'dark' : 'light');
      }

      if (storageValue) {
        setCookie(storageKey, { ...storageValue, [name]: updateValue });

        return;
      }

      setCookie(storageKey, { ...initialSettings, [name]: updateValue });
    },
    [storageKey, initialSettings, setMode]
  );

  const setState = useCallback(
    (updateValue: Partial<SettingsState>) => {
      setStateApp((prev) => ({ ...prev, ...updateValue }));

      const storageValue = getCookie<SettingsState>(storageKey);

      if (updateValue.colorScheme) {
        setMode(updateValue.colorScheme === 'dark' ? 'dark' : 'light');
      }

      if (storageValue) {
        setCookie(storageKey, { ...storageValue, ...updateValue });

        return;
      }

      setCookie(storageKey, { ...initialSettings, ...updateValue });
    },
    [storageKey, initialSettings, setMode]
  );

  return { state: stateApp, resetState, setField, setState };
}
